import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { Check, Loader2, Pencil, X } from "lucide-react";
import type { Locale } from "date-fns/locale";
import { ORDER_DATE_FIELDS, type OrderDateField, type OrderDateKind } from "@threadheads/shared";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useOrderDates } from "@/features/purchase-orders/hooks/useOrderDates";
import {
  dateInputToCalendarDay,
  dateInputToLocalNoonIso,
  describeRevision,
  revisionsNewestFirst,
  summarizeEtaHistory,
  toDateInputValue,
  type EtaRevision,
} from "@/features/purchase-orders/utils/etaHistory";
import { formatDateTime, formatDay, useDateFnsLocale } from "@/lib/format-dates";
import { cn } from "@/lib/utils";

const LABELS: Record<OrderDateField, string> = {
  supplierConfirmedAt: "Supplier confirmed",
  etaDate: "Ready ETA",
  shippedDate: "Ship date",
  arrivalEtaDate: "Arrival ETA",
};

// PR #1989 review: "history", not "revisions": the list includes the first
// "Set to" entry, which the summary line does not count as a revision.
const HISTORY_TITLES: Partial<Record<OrderDateField, string>> = {
  etaDate: "Ready ETA history",
  arrivalEtaDate: "Arrival ETA history",
};

interface PoDatesSectionProps {
  orderId: string;
  locationId: string;
  isTransfer: boolean;
  canEditDates: boolean;
  submittedDate: string | null | undefined;
  /** The buyer's own "Supplier confirmed" record (supplier_confirmed_at). */
  supplierConfirmedAt: string | null | undefined;
  /**
   * The partner portal's acknowledgement. Display fallback for "Supplier
   * confirmed" while the buyer has not entered one; never written from here.
   */
  supplierAcknowledgedAt: string | null | undefined;
  etaDate: string | null | undefined;
  shippedDate: string | null | undefined;
  arrivalEtaDate: string | null | undefined;
  /** System receipt timestamp, shown read-only as "Received". */
  completedDate: string | null | undefined;
}

export function PoDatesSection({
  orderId,
  locationId,
  isTransfer,
  canEditDates,
  submittedDate,
  supplierConfirmedAt,
  supplierAcknowledgedAt,
  etaDate,
  shippedDate,
  arrivalEtaDate,
  completedDate,
}: PoDatesSectionProps) {
  const locale = useDateFnsLocale();
  const [editing, setEditing] = useState<OrderDateField | null>(null);
  const { saveDate, etaHistory, arrivalEtaHistory } = useOrderDates({ orderId, locationId });

  const values: Record<OrderDateField, string | null> = {
    supplierConfirmedAt: supplierConfirmedAt ?? null,
    etaDate: etaDate ?? null,
    shippedDate: shippedDate ?? null,
    arrivalEtaDate: arrivalEtaDate ?? null,
  };
  const revisions: Partial<Record<OrderDateField, EtaRevision[]>> = {
    etaDate: etaHistory,
    arrivalEtaDate: arrivalEtaHistory,
  };
  // PR #1989 review: rows come from the shared field table, so the PO-only
  // rule the backend enforces is the one the panel renders (transfers: Ship date and Arrival ETA).
  const fields = ORDER_DATE_FIELDS.filter((field) => !isTransfer || !field.purchaseOnly);

  return (
    <>
      <DateRow label="Submitted">
        <span className="font-medium tabular-nums">{formatDateTime(submittedDate, locale)}</span>
      </DateRow>
      {fields.map(({ input, kind }) => {
        const historyTitle = HISTORY_TITLES[input];
        const fieldRevisions = revisions[input];
        return (
          <EditableDateRow
            key={input}
            label={LABELS[input]}
            value={values[input]}
            fallback={input === "supplierConfirmedAt" ? (supplierAcknowledgedAt ?? null) : null}
            kind={kind}
            locale={locale}
            canEdit={canEditDates}
            isEditing={editing === input}
            onStartEdit={() => setEditing(input)}
            // Functional update: a save that settles after another row opened must not close that row.
            onStopEdit={() => setEditing((current) => (current === input ? null : current))}
            onSave={(next) => saveDate(input, next)}
            footer={
              historyTitle && fieldRevisions ? (
                <EtaRevisionSummary
                  title={historyTitle}
                  revisions={fieldRevisions}
                  currentEta={values[input]}
                  locale={locale}
                />
              ) : null
            }
          />
        );
      })}
      <DateRow label="Received">
        <span className="font-medium tabular-nums">{formatDateTime(completedDate, locale)}</span>
      </DateRow>
    </>
  );
}

/**
 * One label-over-value row. PR #1989 review: the value line is a fixed `h-6`
 * (the 24px line of a `font-medium` value), and every control placed on it is
 * `h-7` and vertically centred, so it overhangs 2px each side instead of
 * growing the row. Read-only, display and edit rows are therefore all the
 * same height, and entering or leaving edit mode never moves the card.
 */
function DateRow({
  label,
  status,
  footer,
  children,
}: {
  label: string;
  /** Inline message on the label line (no extra line, so no layout shift). */
  status?: ReactNode;
  /** Optional annotation under the value line (the ETA revision summary). */
  footer?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div>
      <p className="flex h-5 items-baseline gap-2 text-sm text-muted-foreground">
        {label}
        {status}
      </p>
      <div className="flex h-6 flex-nowrap items-center gap-1">{children}</div>
      {footer}
    </div>
  );
}

interface EditableDateRowProps {
  label: string;
  /** The row's own stored value. Only this can be cleared. */
  value: string | null;
  /** Shown (marked "by partner") when `value` is empty; never edited or cleared here. */
  fallback: string | null;
  /** 'calendar' = DATE column (sent as YYYY-MM-DD); 'instant' = TIMESTAMPTZ (sent as local noon ISO). */
  kind: OrderDateKind;
  locale: Locale;
  canEdit: boolean;
  isEditing: boolean;
  onStartEdit: () => void;
  onStopEdit: () => void;
  /** Resolves once the order has refetched; rejects (after the hook's toast) on failure. */
  onSave: (next: string | null) => Promise<unknown>;
  footer?: ReactNode;
}

function EditableDateRow({
  label,
  value,
  fallback,
  kind,
  locale,
  canEdit,
  isEditing,
  onStartEdit,
  onStopEdit,
  onSave,
  footer,
}: EditableDateRowProps) {
  const [draft, setDraft] = useState("");
  const [invalid, setInvalid] = useState(false);
  const [pending, setPending] = useState<"save" | "clear" | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const wasEditing = useRef(false);
  const errorId = useId();

  const shown = value ?? fallback;
  const shownDay = toDateInputValue(shown);
  const display = formatDay(shown, locale);
  const fromPartner = value === null && shown !== null;
  const busy = pending !== null;

  // Focus the input on entering edit mode, and again when a failed save
  // re-enables it (a disabled input drops focus).
  useEffect(() => {
    if (isEditing && pending === null) inputRef.current?.focus();
  }, [isEditing, pending]);

  // PR #1989 review: hand focus back to the pencil when editing ends (save,
  // Escape, Cancel), unless it already moved somewhere on purpose (another
  // row's editor), so keyboard users keep their place in the card.
  useEffect(() => {
    if (wasEditing.current && !isEditing) {
      const active = document.activeElement;
      if (!active || active === document.body) triggerRef.current?.focus();
    }
    wasEditing.current = isEditing;
  }, [isEditing]);

  const startEdit = () => {
    setDraft(shownDay);
    setInvalid(false);
    onStartEdit();
  };

  const save = async (next: string | null, action: "save" | "clear") => {
    setPending(action);
    try {
      await onSave(next);
      onStopEdit();
    } catch {
      // The hook raised a destructive toast; keep the row open so the user can retry.
    } finally {
      setPending(null);
    }
  };

  const commit = () => {
    if (busy) return;
    // PR #1989 review: a half-typed date reports value "" with badInput set,
    // and React fires no onChange for it, so read validity at commit time.
    // Saving there would have sent null and wiped the date.
    if (inputRef.current?.validity.badInput) {
      setInvalid(true);
      return;
    }
    // Empty or unchanged: nothing to save. Only the Clear button sends null.
    if (!draft || draft === shownDay) {
      onStopEdit();
      return;
    }
    const next = kind === "calendar" ? dateInputToCalendarDay(draft) : dateInputToLocalNoonIso(draft);
    if (!next) {
      setInvalid(true);
      return;
    }
    void save(next, "save");
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") {
      event.preventDefault();
      if (!busy) onStopEdit();
    } else if (event.key === "Enter" && event.target === inputRef.current) {
      event.preventDefault();
      commit();
    }
  };

  // PR #1989 review: the date and its "by partner" note share a baseline (their
  // font sizes differ, so centring them left the note floating). The wrapper is
  // what gets centred on the row line, so h-7 controls there stay centred too.
  const valueText = (
    <span className="inline-flex items-baseline gap-1.5 font-medium">
      <span className="tabular-nums">{display}</span>
      {fromPartner && <span className="text-xs font-normal text-muted-foreground">by partner</span>}
    </span>
  );

  if (isEditing) {
    return (
      <DateRow
        label={label}
        status={
          invalid ? (
            <span id={errorId} role="alert" className="text-xs text-destructive">
              Enter a full date
            </span>
          ) : null
        }
        footer={footer}
      >
        <div role="group" aria-label={`Edit ${label}`} className="flex items-center gap-1" onKeyDown={handleKeyDown}>
          <Input
            ref={inputRef}
            type="date"
            value={draft}
            onChange={(event) => {
              setDraft(event.target.value);
              setInvalid(false);
            }}
            disabled={busy}
            aria-label={label}
            aria-invalid={invalid || undefined}
            aria-describedby={invalid ? errorId : undefined}
            // -ml-1.5 + px-1.5 keep the date on the display text's x, so opening the editor doesn't nudge it.
            className={cn(
              "-ml-1.5 h-7 w-[140px] px-1.5 py-0 text-sm",
              invalid && "border-destructive focus-visible:border-destructive"
            )}
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7 [&_svg]:size-3.5"
            onClick={commit}
            disabled={busy}
            aria-label={`Save ${label}`}
            aria-busy={pending === "save" || undefined}
          >
            {pending === "save" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check />}
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7 [&_svg]:size-3.5"
            onClick={onStopEdit}
            disabled={busy}
            aria-label={`Cancel editing ${label}`}
          >
            <X />
          </Button>
          {value !== null && (
            <Button
              type="button"
              variant="ghost"
              className="relative h-7 px-2 text-xs text-muted-foreground hover:text-foreground [&_svg]:size-3.5"
              onClick={() => void save(null, "clear")}
              disabled={busy}
              aria-label={`Clear ${label}`}
              aria-busy={pending === "clear" || undefined}
            >
              {/* The spinner overlays the label so the button keeps its width. */}
              <span className={cn(pending === "clear" && "invisible")}>Clear</span>
              {pending === "clear" && <Loader2 className="absolute h-3.5 w-3.5 animate-spin" />}
            </Button>
          )}
        </div>
      </DateRow>
    );
  }

  if (!canEdit) {
    return (
      <DateRow label={label} footer={footer}>
        {valueText}
      </DateRow>
    );
  }

  return (
    <DateRow label={label} footer={footer}>
      <button
        ref={triggerRef}
        type="button"
        onClick={startEdit}
        aria-label={`Edit ${label}: ${shown ? display : "not set"}${fromPartner ? " by partner" : ""}`}
        className="group -mx-1.5 inline-flex h-7 items-center gap-1.5 rounded px-1.5 transition-colors duration-200 ease-out hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring motion-reduce:transition-none"
      >
        {valueText}
        {/* PR #1989 review: the pencil is a quiet hint, revealed on hover or keyboard focus only. */}
        <Pencil
          aria-hidden
          className="h-3 w-3 shrink-0 text-muted-foreground opacity-0 transition-opacity duration-200 ease-out group-hover:opacity-100 group-focus-visible:opacity-100 motion-reduce:transition-none"
        />
      </button>
    </DateRow>
  );
}

function EtaRevisionSummary({
  title,
  revisions,
  currentEta,
  locale,
}: {
  title: string;
  revisions: EtaRevision[];
  currentEta: string | null;
  locale: Locale;
}) {
  const titleId = useId();
  const summary = summarizeEtaHistory(revisions, currentEta, locale);
  if (!summary) return null;

  return (
    <Popover>
      <PopoverTrigger asChild>
        {/* PR #1989 review: the line is 16px; a pseudo-element stretches the hit area to 24px
            (DESIGN.md §16) without moving anything. 2px up stops at the value line's h-7 control,
            6px down falls in the gap before the next row. */}
        <button
          type="button"
          className="relative mt-1 block rounded-sm text-left text-xs leading-4 text-muted-foreground underline-offset-2 transition-colors duration-200 ease-out before:absolute before:inset-x-0 before:-bottom-1.5 before:-top-0.5 before:content-[''] hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring motion-reduce:transition-none"
        >
          {summary}
        </button>
      </PopoverTrigger>
      {/* PR #1989 review: named by its heading, and capped so a long history scrolls inside the popover. */}
      <PopoverContent align="start" aria-labelledby={titleId} className="max-h-80 w-72 overflow-y-auto p-3">
        <p id={titleId} className="mb-2 text-sm font-medium">
          {title}
        </p>
        <ul className="space-y-2">
          {revisionsNewestFirst(revisions).map((revision, index) => (
            <li key={`${revision.at}-${index}`} className="text-sm">
              <p className="font-medium tabular-nums">{describeRevision(revision, locale)}</p>
              <p className="text-xs text-muted-foreground">
                by {revision.actorName ?? "System"} · {formatDay(revision.at, locale)}
              </p>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
